/**
 * TopicsPage — /topics
 * Admin + Teacher: manage Topics (Mavzular).
 * Admin can also merge topics.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/context/AuthContext'
import { useSearchParams } from 'react-router-dom'
import {
  listTopics, createTopic, updateTopic, deleteTopic, mergeTopic,
  listSubjects,
} from '@/api/resources'
import { Button, PageHeader, Badge, EmptyState, Spinner, FIELD_CLS, FIELD_AUTO_CLS, SearchInput, SortTh, TableFooter } from '@/components/ui/primitives'
import usePaginationSort from '@/hooks/usePaginationSort'
import { fetchAllPages } from '@/utils/fetchAll'
import { Pencil, Trash2, GitMerge, X } from 'lucide-react'
import ConfirmDialog from '@/components/ConfirmDialog'

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4">
      <div className="rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold" style={{ color: 'var(--ink)' }}>{title}</h3>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-xl transition-colors" style={{ color: 'var(--ink-3)' }} onMouseEnter={e => e.currentTarget.style.background='var(--bg-soft)'} onMouseLeave={e => e.currentTarget.style.background='transparent'}>
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

export default function TopicsPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const isAdmin = user?.is_superuser || user?.role === 'admin'
  const [searchParams] = useSearchParams()

  const [topics,      setTopics]      = useState([])
  const [loading,     setLoading]     = useState(true)
  const [subjects,    setSubjects]    = useState([])
  const [filterSubject, setFilterSubject] = useState(() => searchParams.get('subject') || '')
  const [search,      setSearch]      = useState('')
  const [modal,       setModal]       = useState(null)   // null | 'create' | topic_obj
  const [mergeModal,  setMergeModal]  = useState(null)   // source topic
  const [confirm,     setConfirm]     = useState(null)
  const [mergeTarget, setMergeTarget] = useState('')
  const [saving,      setSaving]      = useState(false)
  const [error,       setError]       = useState(null)

  const subjectsFetchedRef = useRef(false)
  useEffect(() => {
    if (subjectsFetchedRef.current) return
    subjectsFetchedRef.current = true
    fetchAllPages(listSubjects, { all: 1 }).then(setSubjects)
  }, [])

  useEffect(() => {
    setLoading(true)
    const params = {}
    if (filterSubject) params.subject = filterSubject
    if (search)        params.search  = search
    fetchAllPages(listTopics, params)
      .then(setTopics)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [filterSubject, search])

  async function handleSave(data) {
    setSaving(true); setError(null)
    try {
      if (modal === 'create') {
        const { data: created } = await createTopic(data)
        setTopics(prev => [created, ...prev])
      } else {
        const { data: updated } = await updateTopic(modal.id, data)
        setTopics(prev => prev.map(t => t.id === updated.id ? updated : t))
      }
      setModal(null)
    } catch (e) {
      setError(e.response?.data?.detail || JSON.stringify(e.response?.data) || e.message)
    } finally { setSaving(false) }
  }

  async function handleConfirmDelete() {
    try {
      await deleteTopic(confirm)
      setTopics(prev => prev.filter(t => t.id !== confirm))
    } catch (e) { setError(e.response?.data?.detail || e.message) }
    finally { setConfirm(null) }
  }

  async function handleMerge() {
    if (!mergeTarget || !mergeModal) return
    setSaving(true); setError(null)
    try {
      await mergeTopic(mergeModal.id, Number(mergeTarget))
      // Refresh list
      setTopics(await fetchAllPages(listTopics, { subject: filterSubject || undefined, subsubject: filterSubSubject || undefined }))
      setMergeModal(null); setMergeTarget('')
    } catch (e) {
      setError(e.response?.data?.detail || e.message)
    } finally { setSaving(false) }
  }

  const visibleTopics = topics.filter(t =>
    !search || t.name.toLowerCase().includes(search.toLowerCase())
  )

  const { paginated, page, setPage, pageSize, setPageSize, sortKey, sortDir, handleSort, totalPages, totalCount } = usePaginationSort(visibleTopics)

  return (
    <div className="space-y-4">
      <PageHeader title={t('nav.topics', 'Mavzular')} />

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <SearchInput
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('common.search', 'Qidirish...')}
        />

        <select
          value={filterSubject}
          onChange={e => setFilterSubject(e.target.value)}
          className={FIELD_AUTO_CLS}
        >
          <option value="">{t('subjects.all', 'Barcha fanlar')}</option>
          {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>


        <Button variant="primary" size="sm" onClick={() => setModal('create')}>
          + {t('topics.add', 'Mavzu qo\'shish')}
        </Button>
      </div>

      {/* Table */}
      <div className="tbl-wrap">
        {loading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : visibleTopics.length === 0 ? (
          <EmptyState message={t('topics.empty', 'Mavzu topilmadi')} />
        ) : (
          <>
          <table className="tbl">
            <thead>
              <tr className="">
                <SortTh sortKey="name"         currentKey={sortKey} currentDir={sortDir} onSort={handleSort} className="pl-4 pr-2">{t('topics.name', 'Mavzu nomi')}</SortTh>
                <SortTh sortKey="subject_name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort}>{t('subjects.title', 'Fan')}</SortTh>
                <th className="">{t('topics.status', 'Holat')}</th>
                <th className="">{t('common.actions', 'Amallar')}</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map(topic => (
                <tr key={topic.id} className="">
                  <td className="">
                    <span className="primary">{topic.name}</span>
                    {topic.merged_topic_count > 0 && (
                      <span className="secondary ml-2">({topic.merged_topic_count} birlashtirish)</span>
                    )}
                  </td>
                  <td className=""><span className="secondary">{topic.subject_name || '—'}</span></td>
                  <td className="">
                    {topic.is_merged
                      ? <Badge variant="gray">Birlashtirilgan</Badge>
                      : <Badge variant="green">Faol</Badge>
                    }
                  </td>
                  <td className="tr">
                    <div className="flex gap-1 justify-end">
                      {!topic.is_merged && (
                        <button onClick={() => setModal(topic)} className="tbl-btn tbl-edit" title={t('common.edit')}>
                          <Pencil size={15} />
                        </button>
                      )}
                      {isAdmin && !topic.is_merged && (
                        <button onClick={() => { setMergeModal(topic); setMergeTarget('') }} className="tbl-btn tbl-go" title={t('topics.merge', 'Birlashtir')}>
                          <GitMerge size={15} />
                        </button>
                      )}
                      <button onClick={() => setConfirm(topic.id)} className="tbl-btn tbl-danger" title={t('common.delete')}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TableFooter page={page} totalPages={totalPages} pageSize={pageSize} totalCount={totalCount} onPageChange={setPage} onPageSizeChange={setPageSize} />
          </>
        )}
      </div>

      {/* Create/Edit modal */}
      {modal !== null && (
        <Modal
          title={modal === 'create' ? t('topics.add', 'Mavzu qo\'shish') : t('topics.edit', 'Mavzuni tahrirlash')}
          onClose={() => setModal(null)}
        >
          <TopicForm
            initial={modal === 'create' ? {} : modal}
            subjects={subjects}
            onSave={handleSave}
            onCancel={() => setModal(null)}
            loading={saving}
          />
        </Modal>
      )}

      {confirm !== null && (
        <ConfirmDialog
          title="O'chirishni tasdiqlang"
          message="Haqiqatan ham o'chirmoqchimisiz?"
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirm(null)}
        />
      )}

      {/* Merge modal */}
      {mergeModal && (
        <Modal title={t('topics.mergeTitle', 'Mavzularni birlashtirish')} onClose={() => setMergeModal(null)}>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <strong>"{mergeModal.name}"</strong> {t('topics.mergeInto', 'quyidagi mavzuga birlashtirilsin:')}
          </p>
          <select
            value={mergeTarget}
            onChange={e => setMergeTarget(e.target.value)}
            className={FIELD_CLS}
          >
            <option value="">{t('topics.selectTarget', '— Maqsad mavzuni tanlang —')}</option>
            {topics.filter(t => t.id !== mergeModal.id && !t.is_merged).map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <p className="text-xs text-orange-600 bg-orange-50 rounded-lg px-3 py-2">
            ⚠️ {t('topics.mergeWarning', 'Bu mavzudagi barcha savollar maqsad mavzuga ko\'chiriladi va ushbu mavzu birlashtirilgan deb belgilanadi.')}
          </p>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setMergeModal(null)}>{t('common.cancel', 'Bekor')}</Button>
            <Button variant="primary" disabled={!mergeTarget || saving} onClick={handleMerge}>
              {saving ? t('common.saving', 'Amalga oshirilmoqda...') : t('topics.mergeConfirm', 'Birlashtir')}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}

function TopicForm({ initial = {}, subjects, onSave, onCancel, loading }) {
  const { t } = useTranslation()
  const [name,        setName]        = useState(initial.name || '')
  const [description, setDescription] = useState(initial.description || '')
  const [subjectId,   setSubjectId]   = useState(initial.subject != null ? String(initial.subject) : '')

  return (
    <form onSubmit={e => { e.preventDefault(); onSave({ name, description, subject: subjectId || null }) }} className="space-y-3">
      <div>
        <label className="block text-[12.5px] font-medium text-white/72 mb-1.5">{t('topics.name', 'Mavzu nomi')} *</label>
        <input value={name} onChange={e => setName(e.target.value)} required className={FIELD_CLS} />
      </div>
      <div>
        <label className="block text-[12.5px] font-medium text-white/72 mb-1.5">{t('common.description', 'Tavsif')}</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className={FIELD_CLS} />
      </div>
      <div>
        <label className="block text-[12.5px] font-medium text-white/72 mb-1.5">{t('subjects.title', 'Fan')}</label>
        <select value={subjectId} onChange={e => setSubjectId(e.target.value)} className={FIELD_CLS}>
          <option value="">— {t('subjects.noSubject', 'Fan tanlanmagan')} —</option>
          {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" type="button" onClick={onCancel}>{t('common.cancel', 'Bekor')}</Button>
        <Button variant="primary" type="submit" disabled={loading}>{loading ? t('common.saving', 'Saqlanmoqda...') : t('common.save', 'Saqlash')}</Button>
      </div>
    </form>
  )
}

